import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Home, Building, Calendar, MapPin, Phone, Mail, Users, Shield, Star, Building2, TrendingUp, Search, FileText, Eye, CheckCircle, CreditCard, ChevronRight, Facebook, Twitter, Instagram, Youtube, User, AlertCircle, Info, ArrowRight, Download } from 'lucide-react';

const theme = {
  primaryPink: '#FF1F8E',
  secondaryBlue: '#0066CC',
  accentPurple: '#6B2D8C',
  govBlue: '#003B7A',
  lightPink: '#FFE5F3',
  white: '#FFFFFF',
  cream: '#FFF9F5',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
};




const useIntersectionObserver = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  
  return [ref, isVisible];
};

interface AnimatedCounterProps {
  end: number;
  duration?: number;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useIntersectionObserver(0.2);
  
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(Math.floor(end));
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, isVisible]);
  
  return <span ref={ref as unknown as React.RefObject<HTMLSpanElement>}>{count.toLocaleString()}</span>;
};

const Loader = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    color: theme.white
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      border: `6px solid ${theme.white}40`,
      borderTop: `6px solid ${theme.white}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <h2 style={{ marginTop: '24px', fontSize: '1.8rem', fontWeight: '700' }}>HMDA Booking Portal</h2>
    <p style={{ opacity: 0.9 }}>Loading...</p>
  </div>
);

const HMDAPortal = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [language, setLanguage] = useState('English');
  const [fontSize, setFontSize] = useState('normal');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookingDates, setBookingDates] = useState({ from: '', to: '' });
  const [prefixDays, setPrefixDays] = useState(0);
  const [suffixDays, setSuffixDays] = useState(0);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackingData, setTrackingData] = useState({ bookingNumber: '', mobile: '' });
  const [bookingResults, setBookingResults] = useState<any>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityVenue, setAvailabilityVenue] = useState<any>(null);
  const [availabilityMonth, setAvailabilityMonth] = useState(new Date());

  const RAZORPAY_KEY_ID = 'rzp_test_lXJEZJELQ8emOR'; // Test key - replace with your production key

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const calculateTotal = () => {
    if (!selectedVenue || !bookingDates.from || !bookingDates.to) return { rent: 0, prefixSuffixRent: 0, securityDeposit: 0, gst: 0, total: 0 };
    
    const from = new Date(bookingDates.from);
    const to = new Date(bookingDates.to);
    const days = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    const baseRent = selectedVenue.rate * (selectedVenue.type === 'event' ? days : 1);
    const prefixSuffixRent = (prefixDays + suffixDays) * 10000;
    const totalRent = baseRent + prefixSuffixRent;
    const securityDeposit = totalRent * 0.25;
    const gst = totalRent * 0.18;
    const total = totalRent + securityDeposit + gst;
    
    return { rent: baseRent, prefixSuffixRent, securityDeposit, gst, total };
  };

  const processPayment = async (bookingData: any) => {
    try {
      setIsProcessingPayment(true);

      const costs = calculateTotal();
      
      if (!(window as any).Razorpay) {
        alert('Payment gateway is loading. Please try again in a moment.');
        setIsProcessingPayment(false);
        return;
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: costs.total * 100, // Amount in paise
        currency: 'INR',
        name: 'HMDA Venue Booking',
        description: `${selectedVenue?.name} - ${bookingData.eventName || 'Event Booking'}`,
        image: 'https://hmda.gov.in/images/hmda-logo.png',
        
        handler: function (response: any) {
          setIsProcessingPayment(false);
          alert(`Payment Successful! 
Payment ID: ${response.razorpay_payment_id}

Your booking has been confirmed. You will receive a confirmation email shortly with booking details and receipt.

Booking Reference: HMDA${Date.now()}`);
          
          // Close modal and reset form
          setShowBookingModal(false);
          setBookingDates({ from: '', to: '' });
          setPrefixDays(0);
          setSuffixDays(0);
        },
        
        prefill: {
          name: bookingData.applicantName || '',
          email: bookingData.email || '',
          contact: bookingData.mobile || ''
        },
        
        notes: {
          venue: selectedVenue?.name || '',
          event_name: bookingData.eventName || '',
          booking_from: bookingDates.from,
          booking_to: bookingDates.to,
          prefix_days: prefixDays,
          suffix_days: suffixDays
        },
        
        theme: {
          color: theme.primaryPink
        },
        
        modal: {
          ondismiss: function() {
            setIsProcessingPayment(false);
            alert('Payment cancelled. You can retry the payment anytime.');
          }
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();

    } catch (error) {
      setIsProcessingPayment(false);
      console.error('Payment error:', error);
      alert('Payment initialization failed. Please try again or contact support.');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      ::-webkit-scrollbar { width: 10px; }
      ::-webkit-scrollbar-track { background: ${theme.gray100}; }
      ::-webkit-scrollbar-thumb { background: ${theme.primaryPink}; border-radius: 5px; }
    `;
    document.head.appendChild(style);
    return () => { if (style.parentNode) style.parentNode.removeChild(style); };
  }, []);

  const venues = [
    {
      name: 'Peoples Plaza',
      location: 'Necklace Road, Hussain Sagar',
      rate: 100000,
      rateType: 'per day',
      type: 'event',
      description: 'Premium open-air venue perfect for large exhibitions, corporate events, and cultural programs',
      features: ['5000+ Capacity', 'Prefix/Suffix ₹10k/day', 'Lake View', '24/7 Security', 'Power Backup'],
      image: theme.primaryPink,
      rating: 4.9,
      bookings: 156,
      availability: 'Available'
    },
    {
      name: 'Lumbini Park',
      location: 'Near Secretariat',
      rate: 16000,
      rateType: '4 hrs',
      rate8hr: 30000,
      type: 'shooting',
      description: 'Tranquil park featuring Buddha statue and musical fountain, perfect for cultural film shoots',
      features: ['Buddha Statue', 'Musical Fountain', 'Entry ₹5k + GST', 'Deposit ₹15k (4hr)', 'Night: 2x Rate'],
      image: theme.govBlue,
      rating: 4.8,
      bookings: 98,
      availability: 'Available',
      contact: 'AO: 9154228661'
    },
    {
      name: 'Necklace Road',
      location: 'Hussain Sagar Lake',
      rate: 10000,
      rateType: '4 hrs',
      rate8hr: 20000,
      type: 'shooting',
      description: 'Beautiful waterfront location with stunning city and lake views for commercial shoots',
      features: ['Waterfront', 'City View', 'Deposit ₹15k (4hr)', 'Night: 2x Rate', '8hr: ₹20k + GST'],
      image: theme.accentPurple,
      rating: 4.7,
      bookings: 134,
      availability: 'Available'
    },
    {
      name: 'Sanjeevaiah Park',
      location: 'Tank Bund, Hyderabad',
      rate: 17500,
      rateType: '4 hrs',
      rate8hr: 35000,
      type: 'shooting',
      description: 'Scenic park with natural greenery, ideal for film shooting and photo sessions',
      features: ['Natural Light', 'Multiple Spots', 'Deposit ₹15k (4hr)', 'Greenery', 'Night: 2x Rate'],
      image: theme.secondaryBlue,
      rating: 4.8,
      bookings: 89,
      availability: 'Available'
    }
  ];

  const faqCategories = [
    {
      category: 'Booking & Payment',
      icon: <CreditCard size={24} />,
      color: theme.primaryPink,
      questions: [
        {
          q: 'What are the rental charges for Peoples Plaza?',
          a: 'Peoples Plaza charges ₹1,00,000 (Rupees One Lakh only) per day. Additional prefix/suffix rent is ₹10,000 per day for erection and dismantling. Maximum 3 days each for prefix and suffix (total 6 days). Normal rent applies for days exceeding this limit.'
        },
        {
          q: 'What are the film shooting rates?',
          a: 'Film shooting rates: Lumbini Park (₹16,000-30,000 + ₹5,000 entry fee), Necklace Road (₹10,000-20,000), Sanjeevaiah Park (₹17,500-35,000). All rates are per 4 or 8 hours with 18% GST extra. Night shooting rates are DOUBLE the daytime rates.'
        },
        {
          q: 'What is the security deposit amount?',
          a: 'Security deposit is 1/4th (25%) of the total rent. It must be paid by draft, banker\'s cheque, or pay order in favour of Vice Chairman, BPPA. The deposit is refundable after event completion, subject to no damages and proper venue handover.'
        },
        {
          q: 'What payment methods are accepted?',
          a: 'Payments can be made via draft, banker\'s cheque, pay order in favour of Vice Chairman BPPA, or online through UPI, credit/debit cards, and net banking.'
        },
        {
          q: 'When should I pay for short-term bookings?',
          a: 'For short-term events (≤30 days): Applications with prescribed rent must be submitted 2 months before the event date. Full rent is required for blocking dates. Allotment is confirmed after receipt of full rent along with security deposit.'
        },
        {
          q: 'What is the payment schedule for long-term bookings?',
          a: 'For events longer than 30 days: Applications accepted 4 months before event. Pay 50% advance initially for date blocking. Remaining 50% plus security deposit must be paid 1 month before event. Maximum booking restricted to 30 days; variety of events encouraged.'
        }
      ]
    },
    {
      category: 'Cancellation & Changes',
      icon: <AlertCircle size={24} />,
      color: theme.error,
      questions: [
        {
          q: 'What is the cancellation policy?',
          a: 'Applicant cancellation: 50% of total rent paid is forfeited, 50% refunded without interest. Government/BPPA/Police cancellation for unavoidable circumstances: 100% refund without interest. No exemption if event is closed partially during daytime. Once application accepted with prescribed rentals, it is binding on the applicant for blocking the date.'
        },
        {
          q: 'Can I postpone my booking?',
          a: 'Yes, under special circumstances with valid reasons, postponement is permissible ONLY ONCE. Additional charge: 25% of total rent. Subject to date availability and at sole discretion of BPPA. Same conditions apply as per Sl.No.4(a) & 4(b) regarding payment schedules.'
        },
        {
          q: 'Can I prepone my event date?',
          a: 'Preponing follows the same rules as postponement - allowed once with 25% additional charge, subject to availability and approval with valid justification.'
        },
        {
          q: 'What if I don\'t show up on booking date?',
          a: 'No-show bookings result in full forfeiture of all payments including security deposit. No refunds or rescheduling allowed for no-shows.'
        }
      ]
    },
    {
      category: 'Booking Requirements',
      icon: <FileText size={24} />,
      color: theme.secondaryBlue,
      questions: [
        {
          q: 'What documents are required for booking?',
          a: 'Required: ID proof (Aadhar/PAN/Passport/Driving License - upload required), GST number (if applicable), event name, applicant name, mobile number, alternate mobile, email ID, booking dates (From & To), prefix days, suffix days. You must physically check the venue before proceeding with booking.'
        },
        {
          q: 'How far in advance can I book?',
          a: 'Bookings can be made up to 180 days (6 months) in advance. Minimum advance booking: 60 days for short events, 120 days for long events (>30 days).'
        },
        {
          q: 'What is the maximum booking duration?',
          a: 'Maximum booking duration is 30 days. Longer bookings are considered on case-by-case basis with special approval from BPPA. Variety of events shall be encouraged instead of monotonous activities.'
        },
        {
          q: 'Do I need to visit physically before booking?',
          a: 'Yes, it is recommended to physically check the venue before proceeding with booking to verify suitability for your event requirements.'
        }
      ]
    },
    {
      category: 'Event Rules & Restrictions',
      icon: <Shield size={24} />,
      color: theme.warning,
      questions: [
        {
          q: 'Are religious or political activities allowed?',
          a: 'No. Religious and political activities, or any activities objectionable to government or civil society are strictly prohibited at all HMDA venues. The organizer should not conduct any religious/political activity.'
        },
        {
          q: 'Is alcohol allowed at venues?',
          a: 'No. Sale and consumption of liquor is strictly prohibited at all HMDA-managed venues without exception.'
        },
        {
          q: 'Can I transfer my booking to someone else?',
          a: 'No. Bookings are non-transferable. The allottee/applicant is not entitled to transfer the allotment to others. Events must be conducted in the name/banners shown in the allotment order. Transfer attempts result in booking cancellation.'
        },
        {
          q: 'What are night shooting rates?',
          a: 'Night shooting rates are DOUBLE the regular daytime rates. Night time is typically defined as after regular working hours (post 6 PM).'
        }
      ]
    },
    {
      category: 'Responsibilities & Compliance',
      icon: <CheckCircle size={24} />,
      color: theme.success,
      questions: [
        {
          q: 'What permissions do I need before the event?',
          a: 'Mandatory permissions required from: Local Police Department, Fire Authority, and other relevant state/central government bodies. Police-fixed timings must be strictly followed. The organizer shall take all mandatory permissions from concerned State/Central Government and other Local bodies.'
        },
        {
          q: 'Who is responsible for waste management?',
          a: 'Event organizer is fully responsible. The ground is located abutting to Hussain Sagar Lake, therefore must ensure no waste material, plastic bags, cups are thrown into the lake. Must provide dustbins, arrange garbage lifting, provide mobile toilets, and ensure no waste enters lake. Violations attract penalties up to Rs. 10,000.'
        },
        {
          q: 'What medical facilities are required?',
          a: 'Organizer must provide minimum medical facilities including first aid at the venue to handle any medical emergencies during the event.'
        },
        {
          q: 'Who handles event security?',
          a: 'Organizer must engage private security personnel at own cost for traffic control and maintaining peace near and inside the venue.'
        },
        {
          q: 'What about parking arrangements?',
          a: 'No vehicles/two-wheelers should be parked on roads abutting the venue or inside the ground. All vehicles must be diverted to designated parking lots with parking fees paid to BPPA/HMDA.'
        },
        {
          q: 'Do I need to arrange power supply?',
          a: 'Yes, organizer must make own arrangements for required power supply and pay charges directly to the concerned electricity agency.'
        }
      ]
    },
    {
      category: 'Penalties & Violations',
      icon: <AlertCircle size={24} />,
      color: theme.error,
      questions: [
        {
          q: 'What is the penalty for not cleaning the venue?',
          a: 'Rs. 5,000 will be forfeited from security deposit if the ground is not cleaned properly after the event.'
        },
        {
          q: 'What happens if I don\'t vacate on time?',
          a: 'Full security deposit will be forfeited if premises are not cleared/handed over within stipulated time. For extra/excess time utilization of ground, full amount of that day must be paid, otherwise your materials will be seized.'
        },
        {
          q: 'What are penalties for environmental violations?',
          a: 'Violations including waste disposal, lake pollution, or missing dustbins attract penalties which shall not be less than Rs. 10,000 in addition to damage cost recovery from security deposit.'
        },
        {
          q: 'What is blacklisting and when does it happen?',
          a: 'If any violations are noticed, not only penalties but the applicant/allottee will be blacklisted and will not be entitled for allotment of the site in future. Serious violations result in permanent barring from future bookings at all HMDA venues.'
        }
      ]
    },
    {
      category: 'Safety & Liability',
      icon: <Shield size={24} />,
      color: theme.accentPurple,
      questions: [
        {
          q: 'Who is liable for accidents during events?',
          a: 'In case of any accident/injury/death to any person or loss of property of any individual, the organizer shall be solely held responsible and should pay compensation as required under existing laws. The BPPA/HMDA shall not be dragged into any legal dispute concerning the matter.'
        },
        {
          q: 'What safety standards must be followed?',
          a: 'All safety standards shall be scrupulously followed in case of any equipment, amusement rides and miscellaneous items. BPPA/HMDA shall not be held responsible in any manner with regards to maintenance of safety standards.'
        },
        {
          q: 'What precautions are mandatory?',
          a: 'All mandatory precautions and preventive measures should be taken to protect the property and life of citizens/visitors. This is organizer\'s sole responsibility.'
        }
      ]
    },
    {
      category: 'Additional Information',
      icon: <Info size={24} />,
      color: theme.govBlue,
      questions: [
        {
          q: 'What defines "one day" in booking terms?',
          a: 'One day means 24 hours from 12:00 AM (Midnight) to 12:00 AM (Midnight) the next day.'
        },
        {
          q: 'What about GST charges?',
          a: 'GST is charged at 18% of the total Rental Amount excluding Security Deposit (refundable). The GST amount is calculated on the base rent only.'
        },
        {
          q: 'What are prefix and suffix days options?',
          a: 'Prefix days options: For 1 to 2 days - no Prefix and Suffix days; For 3 to 7 days - 1 Prefix day and 1 Suffix day; For 8 to 15 days - 2 Prefix days and 2 Suffix days; For Above 15 days - 3 Prefix days and 3 Suffix days. These are for erection and dismantling at ₹10,000 per day.'
        },
        {
          q: 'Are there reserved seats for officials?',
          a: 'Yes, when seating is arranged, 3 seats/chairs must be reserved amongst audience in favour of personnel/Officers of BPPA/HMDA in order to facilitate monitoring.'
        },
        {
          q: 'Can officials access the venue during events?',
          a: 'Yes, the Officers/Security Personnel of BPPA/HMDA shall have access to the venue including the stage at all times and even during the actual performance.'
        },
        {
          q: 'Can BPPA modify conditions after booking?',
          a: 'Yes, BPPA/HMDA reserves the right to add more conditions or modify the conditions at any time till the date of release of Security Deposit by BPPA/HMDA. The organizer is bound to comply with new/modified conditions.'
        },
        {
          q: 'Can BPPA cancel confirmed bookings?',
          a: 'Yes, if Government requires the venue for important state functions/programmes, the permission given will be cancelled by BPPA/HMDA. No responsibility will be taken by BPPA/HMDA for any loss caused to organizer. BPPA/HMDA reserves the right to cancel the permission given at any time without assigning any reason.'
        },
        {
          q: 'Who pays applicable taxes?',
          a: 'The tax as applicable shall be payable by the event manager/allottee to the respective Departments and BPPA will not be responsible.'
        },
        {
          q: 'Are there exemptions or reductions in rent?',
          a: 'No exemption or reduction in rent will be considered under any circumstances. However, 50% concession can be extended to Government Departments only. No concession for Semi-Government agencies/Corporations. Exemption considered only if event closed at instance of BPPA/Govt/Police for unavoidable circumstances.'
        },
        {
          q: 'Can I extend my event?',
          a: 'If any allottee/applicant intends to extend the event, the same will be considered subject to availability and at the sole discretion of BPPA, well in advance and on payment of rent for the extended period.'
        },
        {
          q: 'How are damages recovered?',
          a: 'Any damages caused by applicant/allottee, the cost of damages will be recovered from security deposit and balance (if any) refunded. However, if cost of damage is more than security deposit, the balance must be paid immediately.'
        },
        {
          q: 'What happens if rent is increased?',
          a: 'Whenever rent is increased, it will be applicable only to prospective applicants and will not be applicable where dates are already blocked/allotted.'
        },
        {
          q: 'Must the organizer clean the area?',
          a: 'Yes, the organizer shall get the area cleaned and leveled at own expense and make it usable for conduct of the programme.'
        },
        {
          q: 'Can the organizer fill up the area?',
          a: 'No, the organizer shall not fill up the area with any material or any act which pollute the area and the lake as per the Hon\'ble Supreme Court directions.'
        }
      ]
    }
  ];

  const filteredVenues = selectedType === 'all' ? venues : venues.filter(v => v.type === selectedType);

  if (isLoading) return <Loader />;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Top Bar */}
      <div style={{ background: theme.gray900, color: theme.white, padding: isMobile ? '8px 16px' : '10px 24px', fontSize: '13px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{ background: theme.gray800, color: theme.white, border: `1px solid ${theme.gray700}`, padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              <option value="English">English</option>
              <option value="Telugu">తెలుగు</option>
              <option value="Hindi">हिंदी</option>
            </select>

            <div style={{ display: 'flex', gap: '4px' }}>
              {['A-', 'A', 'A+'].map((size, idx) => (
                <button
                  key={size}
                  onClick={() => setFontSize(['small', 'normal', 'large'][idx])}
                  style={{
                    background: fontSize === ['small', 'normal', 'large'][idx] ? theme.primaryPink : theme.gray800,
                    color: theme.white,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '20px', fontSize: '12px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Phone size={14} /> 040-27018115
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Mail size={14} /> support@hmda.gov.in
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{
        background: theme.white,
        boxShadow: isScrolled ? `0 4px 20px ${theme.gray200}` : 'none',
        position: 'sticky',
        top: 0,
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '12px 16px' : '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '50px',
              height: '50px',
              background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.white,
              fontWeight: '700',
              fontSize: '18px',
              boxShadow: `0 4px 12px ${theme.primaryPink}40`
            }}>
              HMDA
            </div>
            <div>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.secondaryBlue})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1
              }}>
                Venue Booking
              </div>
              <div style={{ fontSize: '0.7rem', color: theme.gray600, marginTop: '2px' }}>
                Hyderabad Metropolitan Development Authority
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              display: isMobile ? 'block' : 'none',
              background: 'none',
              border: 'none',
              color: theme.gray900,
              cursor: 'pointer'
            }}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div style={{
            display: isMobile && !isMenuOpen ? 'none' : 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '8px',
            alignItems: isMobile ? 'stretch' : 'center',
            position: isMobile ? 'absolute' : 'static',
            top: isMobile ? '100%' : 'auto',
            left: 0,
            right: 0,
            background: isMobile ? theme.white : 'transparent',
            padding: isMobile ? '24px' : '0',
            boxShadow: isMobile ? `0 4px 20px ${theme.gray200}` : 'none'
          }}>
            {[
              { label: 'Home', href: '#home', icon: <Home size={18} /> },
              { label: 'Venues', href: '#venues', icon: <Building size={18} /> },
              { label: 'Check Availability', href: '#availability', icon: <Calendar size={18} />, onClick: () => setShowAvailabilityModal(true) },
              { label: 'Book Now', href: '#book', icon: <Calendar size={18} />, highlight: true },
              { label: 'My Bookings', href: '#mybookings', icon: <FileText size={18} />, onClick: () => setShowTrackModal(true) },
              { label: 'Track', href: '#track', icon: <Search size={18} />, onClick: () => setShowTrackModal(true) }
            ].map((item, idx) => (
              <a
                key={idx}
                href={item.href}
                onClick={(e) => {
                  if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: item.highlight ? theme.white : theme.gray900,
                  background: item.highlight ? `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})` : 'transparent',
                  textDecoration: 'none',
                  fontWeight: '600',
                  padding: item.highlight ? '10px 20px' : '10px 16px',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease',
                  fontSize: '0.95rem'
                }}
                onMouseEnter={(e) => {
                  if (!item.highlight) {
                    e.currentTarget.style.background = theme.lightPink;
                    e.currentTarget.style.color = theme.primaryPink;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!item.highlight) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.gray900;
                  }
                }}
              >
                {item.icon}
                {item.label}
              </a>
            ))}

            <button style={{
              background: 'transparent',
              border: `2px solid ${theme.primaryPink}`,
              color: theme.primaryPink,
              padding: '8px 20px',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginLeft: isMobile ? '0' : '16px'
            }}>
              <User size={18} />
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Featured Venues Section */}
      <section style={{ padding: '60px 24px', background: theme.white }} id="venues">
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3.8rem',
              fontWeight: '900',
              marginBottom: '20px',
              background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.secondaryBlue})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Featured Venues
            </h2>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '32px'
            }}>
              {[
                { label: 'All Venues', value: 'all' },
                { label: 'Events', value: 'event' },
                { label: 'Film Shooting', value: 'shooting' }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setSelectedType(filter.value)}
                  style={{
                    background: selectedType === filter.value 
                      ? `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`
                      : theme.white,
                    color: selectedType === filter.value ? theme.white : theme.gray700,
                    border: selectedType === filter.value ? 'none' : `2px solid ${theme.gray200}`,
                    padding: '12px 28px',
                    borderRadius: '30px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '32px'
          }}>
            {filteredVenues.map((venue, idx) => (
              <div
                key={idx}
                style={{
                  background: theme.white,
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                  border: `2px solid ${theme.gray100}`,
                  transition: 'all 0.4s ease',
                  animation: 'fadeIn 0.6s ease forwards',
                  animationDelay: `${idx * 0.1}s`,
                  opacity: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)';
                }}
              >
                <div style={{
                  height: '240px',
                  background: `linear-gradient(135deg, ${venue.image}, ${venue.image}dd)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.white,
                  position: 'relative'
                }}>
                  <Building2 size={100} style={{ opacity: 0.3 }} />
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    background: venue.availability === 'Available' ? theme.success : theme.error,
                    color: theme.white,
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: theme.white,
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }} />
                    {venue.availability}
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    background: 'rgba(255,255,255,0.25)',
                    backdropFilter: 'blur(10px)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <Star size={16} fill={theme.warning} style={{ color: theme.warning }} />
                    <span style={{ fontWeight: '700', fontSize: '14px' }}>{venue.rating}</span>
                  </div>
                </div>

                <div style={{ padding: '32px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '8px', color: theme.gray900 }}>
                        {venue.name}
                      </h3>
                      <p style={{ 
                        color: theme.gray600, 
                        fontSize: '0.95rem', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        fontWeight: '500'
                      }}>
                        <MapPin size={16} /> {venue.location}
                      </p>
                    </div>
                    <span style={{
                      background: venue.type === 'event' ? `${theme.primaryPink}15` : `${theme.secondaryBlue}15`,
                      color: venue.type === 'event' ? theme.primaryPink : theme.secondaryBlue,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {venue.type === 'event' ? 'Event' : 'Shooting'}
                    </span>
                  </div>

                  <p style={{ 
                    color: theme.gray700, 
                    marginBottom: '24px', 
                    lineHeight: 1.7,
                    fontSize: '0.95rem'
                  }}>
                    {venue.description}
                  </p>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '24px'
                  }}>
                    {venue.features.map((feature, i) => (
                      <span
                        key={i}
                        style={{
                          background: theme.gray100,
                          padding: '8px 14px',
                          borderRadius: '10px',
                          fontSize: '13px',
                          color: theme.gray700,
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <CheckCircle size={14} style={{ color: theme.success }} />
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '16px',
                    marginBottom: '24px',
                    padding: '20px',
                    background: `${venue.image}08`,
                    borderRadius: '16px',
                    border: `1px solid ${venue.image}20`
                  }}>
                    <div>
                      <p style={{ fontSize: '11px', color: theme.gray600, marginBottom: '6px', fontWeight: '600' }}>
                        RATE
                      </p>
                      <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
                        ₹{venue.rate.toLocaleString()}
                      </p>
                      <p style={{ fontSize: '11px', color: theme.gray600 }}>{venue.rateType}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '11px', color: theme.gray600, marginBottom: '6px', fontWeight: '600' }}>
                        BOOKINGS
                      </p>
                      <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
                        {venue.bookings}+
                      </p>
                      <p style={{ fontSize: '11px', color: theme.gray600 }}>this month</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '11px', color: theme.gray600, marginBottom: '6px', fontWeight: '600' }}>
                        GST
                      </p>
                      <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
                        18%
                      </p>
                      <p style={{ fontSize: '11px', color: theme.gray600 }}>extra</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button style={{
                      flex: 1,
                      background: `linear-gradient(135deg, ${venue.image}, ${venue.image}dd)`,
                      color: theme.white,
                      border: 'none',
                      padding: '16px',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'transform 0.2s ease'
                    }}
                    onClick={() => {
                      setSelectedVenue(venue);
                      setShowBookingModal(true);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                      <Calendar size={20} />
                      Book Now
                    </button>
                    <button style={{
                      background: theme.white,
                      color: venue.image,
                      border: `2px solid ${venue.image}`,
                      padding: '16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700'
                    }}>
                      <Eye size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section style={{ padding: '100px 24px', background: `linear-gradient(180deg, ${theme.white} 0%, ${theme.cream} 100%)` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3.8rem',
              fontWeight: '900',
              marginBottom: '20px',
              background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.secondaryBlue})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              How It Works
            </h2>
            <p style={{ fontSize: '1.2rem', color: theme.gray600, maxWidth: '600px', margin: '0 auto' }}>
              Book your venue in 4 simple steps
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '32px',
            position: 'relative'
          }}>
            {!isMobile && (
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '12.5%',
                right: '12.5%',
                height: '4px',
                background: `linear-gradient(90deg, ${theme.primaryPink}, ${theme.secondaryBlue})`,
                opacity: 0.2,
                zIndex: 0
              }} />
            )}

            {[
              { icon: <Search size={32} />, title: 'Search Venue', desc: 'Browse and select from 12+ premium locations', color: theme.primaryPink },
              { icon: <Calendar size={32} />, title: 'Check Dates', desc: 'Verify availability for your preferred dates', color: theme.secondaryBlue },
              { icon: <FileText size={32} />, title: 'Fill Details', desc: 'Complete booking form with event information', color: theme.accentPurple },
              { icon: <CreditCard size={32} />, title: 'Make Payment', desc: 'Secure online payment with instant confirmation', color: theme.govBlue }
            ].map((step, idx) => (
              <div
                key={idx}
                style={{
                  background: theme.white,
                  padding: '40px 24px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1,
                  border: `2px solid ${theme.gray100}`,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.white,
                  margin: '0 auto 24px',
                  boxShadow: `0 10px 25px ${step.color}40`,
                  position: 'relative'
                }}>
                  {step.icon}
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '32px',
                    height: '32px',
                    background: theme.white,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '800',
                    fontSize: '14px',
                    color: step.color,
                    border: `3px solid ${step.color}`
                  }}>
                    {idx + 1}
                  </div>
                </div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '12px', color: theme.gray900 }}>
                  {step.title}
                </h3>
                <p style={{ color: theme.gray600, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: '100px 24px',
        background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
        color: theme.white,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
          pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{ fontSize: isMobile ? '2.5rem' : '3.8rem', fontWeight: '900', marginBottom: '16px' }}>
              Our Impact
            </h2>
            <p style={{ fontSize: '1.2rem', opacity: 0.95 }}>
              Trusted by thousands across Hyderabad
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: '32px'
          }}>
            {[
              { icon: <Building2 size={36} />, value: 12, label: 'Premium Venues', suffix: '+' },
              { icon: <Users size={36} />, value: 5000, label: 'Happy Customers', suffix: '+' },
              { icon: <Calendar size={36} />, value: 156, label: 'Monthly Bookings', suffix: '' },
              { icon: <TrendingUp size={36} />, value: 98, label: 'Success Rate', suffix: '%' }
            ].map((stat, idx) => (
              <div
                key={idx}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '24px',
                  padding: '40px 24px',
                  textAlign: 'center',
                  border: '2px solid rgba(255,255,255,0.25)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ marginBottom: '20px' }}>{stat.icon}</div>
                <h3 style={{ fontSize: '3rem', fontWeight: '900', marginBottom: '8px' }}>
                  <AnimatedCounter end={stat.value} />{stat.suffix}
                </h3>
                <p style={{ fontSize: '1rem', opacity: 0.95, fontWeight: '600' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '100px 24px', background: theme.white }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <div style={{
              display: 'inline-block',
              background: `${theme.primaryPink}10`,
              padding: '8px 20px',
              borderRadius: '30px',
              marginBottom: '20px',
              border: `2px solid ${theme.lightPink}`
            }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: theme.primaryPink }}>
                TERMS & CONDITIONS
              </span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3.8rem',
              fontWeight: '900',
              marginBottom: '20px',
              background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.secondaryBlue})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Frequently Asked Questions
            </h2>

            <p style={{ fontSize: '1.2rem', color: theme.gray600, maxWidth: '700px', margin: '0 auto' }}>
              Everything you need to know about booking HMDA venues
            </p>
          </div>

          <div style={{ display: 'grid', gap: '24px' }}>
            {faqCategories.map((category, catIdx) => (
              <div
                key={catIdx}
                style={{
                  background: theme.white,
                  borderRadius: '20px',
                  border: `2px solid ${theme.gray100}`,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
              >
                <div style={{
                  background: `${category.color}10`,
                  padding: '24px',
                  borderBottom: `2px solid ${theme.gray100}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: category.color,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.white
                  }}>
                    {category.icon}
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: theme.gray900
                  }}>
                    {category.category}
                  </h3>
                </div>

                <div style={{ padding: '8px' }}>
                  {category.questions.map((faq, qIdx) => {
                    const index = `${catIdx}-${qIdx}`;
                    const isOpen = openFaq === index;

                    return (
                      <div
                        key={qIdx}
                        style={{
                          borderBottom: qIdx < category.questions.length - 1 ? `1px solid ${theme.gray100}` : 'none'
                        }}
                      >
                        <button
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          style={{
                            width: '100%',
                            padding: '20px 24px',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '16px',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme.gray50}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <span style={{
                            fontSize: '1.05rem',
                            fontWeight: '700',
                            color: theme.gray900,
                            flex: 1
                          }}>
                            {faq.q}
                          </span>
                          <ChevronRight
                            size={24}
                            style={{
                              color: category.color,
                              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                              flexShrink: 0
                            }}
                          />
                        </button>

                        <div style={{
                          maxHeight: isOpen ? '1000px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease'
                        }}>
                          <div style={{
                            padding: '0 24px 24px 24px',
                            color: theme.gray700,
                            lineHeight: 1.8,
                            fontSize: '0.95rem'
                          }}>
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '64px',
            padding: '40px',
            background: `linear-gradient(135deg, ${theme.primaryPink}10, ${theme.secondaryBlue}10)`,
            borderRadius: '20px',
            border: `2px solid ${theme.lightPink}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '16px', color: theme.gray900 }}>
              Still Have Questions?
            </h3>
            <p style={{ fontSize: '1.1rem', color: theme.gray600, marginBottom: '24px' }}>
              Our support team is here to help you
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:04027018115" style={{
                background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
                color: theme.white,
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <Phone size={20} />
                Call: 040-27018115
              </a>
              <a href="mailto:support@hmda.gov.in" style={{
                background: theme.white,
                color: theme.primaryPink,
                border: `2px solid ${theme.primaryPink}`,
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <Mail size={20} />
                Email Support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: theme.gray900, color: theme.gray100, padding: isMobile ? '60px 24px' : '80px 24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '48px',
            marginBottom: '48px'
          }}>
            <div>
              <h3 style={{ color: theme.white, fontSize: '1.5rem', fontWeight: '800', marginBottom: '16px' }}>
                HMDA Booking
              </h3>
              <p style={{ marginBottom: '20px', lineHeight: 1.7, fontSize: '0.95rem' }}>
                Official Hyderabad Metropolitan Development Authority venue booking platform
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[Facebook, Twitter, Instagram, Youtube].map((Icon, idx) => (
                  <a key={idx} href="#" style={{
                    width: '42px',
                    height: '42px',
                    background: theme.gray800,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.gray100,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.primaryPink;
                    e.currentTarget.style.color = theme.white;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = theme.gray800;
                    e.currentTarget.style.color = theme.gray100;
                  }}>
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>

            {[
              { title: 'Quick Links', links: ['Book Venue', 'Check Availability', 'My Bookings', 'Guidelines', 'FAQs'] },
              { title: 'Venues', links: ['Peoples Plaza', 'Necklace Road', 'Sanjeevaiah Park', 'Lumbini Park', 'NTR Gardens'] },
              { title: 'Support', links: ['Contact Us', 'Help Center', 'Terms', 'Privacy', 'Sitemap'] }
            ].map((section, idx) => (
              <div key={idx}>
                <h4 style={{ color: theme.white, fontSize: '1.2rem', fontWeight: '700', marginBottom: '20px' }}>
                  {section.title}
                </h4>
                <ul style={{ listStyle: 'none' }}>
                  {section.links.map((link, i) => (
                    <li key={i} style={{ marginBottom: '12px' }}>
                      <a href="#" style={{
                        color: theme.gray100,
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = theme.primaryPink}
                      onMouseLeave={(e) => e.currentTarget.style.color = theme.gray100}>
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{
            borderTop: `1px solid ${theme.gray800}`,
            paddingTop: '32px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px' }}>
              © 2025 Hyderabad Metropolitan Development Authority. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      {showBookingModal && selectedVenue && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '24px',
          overflowY: 'auto'
        }}
        onClick={() => setShowBookingModal(false)}>
          <div style={{
            background: theme.white,
            borderRadius: '24px',
            maxWidth: '1200px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '32px',
              borderBottom: `2px solid ${theme.gray100}`,
              background: `linear-gradient(135deg, ${selectedVenue.image}15, ${selectedVenue.image}05)`,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: theme.gray900,
                  marginBottom: '8px'
                }}>
                  New Booking - {selectedVenue.name}
                </h2>
                <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  {selectedVenue.location}
                </p>
              </div>
              <button
                onClick={() => setShowBookingModal(false)}
                style={{
                  background: theme.gray100,
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.gray700
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
                gap: '32px'
              }}>
                {/* Left Column - Form */}
                <div>
                  <div style={{
                    background: `${selectedVenue.image}08`,
                    padding: '24px',
                    borderRadius: '16px',
                    marginBottom: '24px',
                    border: `2px solid ${selectedVenue.image}20`
                  }}>
                    <h3 style={{
                      fontSize: '1.2rem',
                      fontWeight: '800',
                      color: theme.gray900,
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Calendar size={20} />
                      Availability Calendar - {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>

                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                        style={{
                          background: theme.white,
                          border: `2px solid ${theme.gray200}`,
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        &lt; Prev
                      </button>
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                        style={{
                          background: theme.white,
                          border: `2px solid ${theme.gray200}`,
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Next &gt;
                      </button>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '8px',
                      textAlign: 'center'
                    }}>
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <div key={day} style={{
                          fontWeight: '700',
                          fontSize: '0.85rem',
                          color: theme.gray700,
                          padding: '8px 0'
                        }}>
                          {day}
                        </div>
                      ))}
                      {(() => {
                        const { firstDay, daysInMonth } = getMonthDays(currentMonth);
                        const days = [];
                        for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
                          days.push(<div key={`empty-${i}`} />);
                        }
                        for (let day = 1; day <= daysInMonth; day++) {
                          const isToday = new Date().getDate() === day && 
                                         new Date().getMonth() === currentMonth.getMonth() &&
                                         new Date().getFullYear() === currentMonth.getFullYear();
                          const isPast = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < new Date();
                          days.push(
                            <div
                              key={day}
                              style={{
                                padding: '12px',
                                borderRadius: '8px',
                                background: isPast ? theme.gray100 : isToday ? selectedVenue.image : theme.white,
                                color: isPast ? theme.gray100 : isToday ? theme.white : theme.gray900,
                                cursor: isPast ? 'not-allowed' : 'pointer',
                                fontWeight: isToday ? '800' : '600',
                                fontSize: '0.9rem',
                                border: `2px solid ${isPast ? theme.gray200 : theme.gray200}`
                              }}
                            >
                              {day}
                            </div>
                          );
                        }
                        return days;
                      })()}
                    </div>

                    <div style={{ marginTop: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '16px', background: theme.gray900, borderRadius: '4px' }} />
                        <span>Available</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '16px', background: theme.error, borderRadius: '4px' }} />
                        <span>Booked</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '16px', background: theme.gray100, borderRadius: '4px' }} />
                        <span>Past</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{
                      fontSize: '1.2rem',
                      fontWeight: '800',
                      color: theme.gray900,
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FileText size={20} />
                      Booking Details
                    </h3>

                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Booking From *
                          </label>
                          <input
                            type="date"
                            value={bookingDates.from}
                            onChange={(e) => setBookingDates({ ...bookingDates, from: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Booking To *
                          </label>
                          <input
                            type="date"
                            value={bookingDates.to}
                            onChange={(e) => setBookingDates({ ...bookingDates, to: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Prefix Days
                          </label>
                          <select
                            value={prefixDays}
                            onChange={(e) => setPrefixDays(Number(e.target.value))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              cursor: 'pointer'
                            }}
                          >
                            <option value={0}>No Prefix</option>
                            <option value={1}>1 Day (₹10,000)</option>
                            <option value={2}>2 Days (₹20,000)</option>
                            <option value={3}>3 Days (₹30,000)</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Suffix Days
                          </label>
                          <select
                            value={suffixDays}
                            onChange={(e) => setSuffixDays(Number(e.target.value))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              cursor: 'pointer'
                            }}
                          >
                            <option value={0}>No Suffix</option>
                            <option value={1}>1 Day (₹10,000)</option>
                            <option value={2}>2 Days (₹20,000)</option>
                            <option value={3}>3 Days (₹30,000)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                          Event Name *
                        </label>
                        <input
                          type="text"
                          placeholder="Enter event name"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: `2px solid ${theme.gray200}`,
                            borderRadius: '8px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Applicant Name *
                          </label>
                          <input
                            type="text"
                            placeholder="Enter full name"
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                            Mobile Number *
                          </label>
                          <input
                            type="tel"
                            placeholder="Enter mobile number"
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: `2px solid ${theme.gray200}`,
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                          Email ID *
                        </label>
                        <input
                          type="email"
                          placeholder="Enter email address"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: `2px solid ${theme.gray200}`,
                            borderRadius: '8px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                          GST Number (Optional)
                        </label>
                        <input
                          type="text"
                          placeholder="Enter GST number if applicable"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: `2px solid ${theme.gray200}`,
                            borderRadius: '8px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Charges */}
                <div>
                  <div style={{
                    background: theme.white,
                    border: `2px solid ${selectedVenue.image}`,
                    borderRadius: '16px',
                    padding: '24px',
                    position: 'sticky',
                    top: '120px'
                  }}>
                    <h3 style={{
                      fontSize: '1.3rem',
                      fontWeight: '800',
                      color: theme.gray900,
                      marginBottom: '20px',
                      textAlign: 'center'
                    }}>
                      CHARGES
                    </h3>

                    {(() => {
                      const costs = calculateTotal();
                      return (
                        <div style={{ display: 'grid', gap: '16px' }}>
                          <div style={{
                            padding: '16px',
                            background: theme.gray50,
                            borderRadius: '12px'
                          }}>
                            <div style={{ fontSize: '0.85rem', color: theme.gray600, marginBottom: '4px' }}>
                              RENT Amount
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: theme.gray900 }}>
                              ₹{costs.rent.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.gray600, marginTop: '4px' }}>
                              {selectedVenue.rateType}
                            </div>
                          </div>

                          <div style={{
                            padding: '16px',
                            background: theme.gray50,
                            borderRadius: '12px'
                          }}>
                            <div style={{ fontSize: '0.85rem', color: theme.gray600, marginBottom: '4px' }}>
                              Prefix and Suffix Rent
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: theme.gray900 }}>
                              ₹{costs.prefixSuffixRent.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.gray600, marginTop: '4px' }}>
                              Rs.10,000/- per day
                            </div>
                          </div>

                          <div style={{
                            padding: '16px',
                            background: theme.gray50,
                            borderRadius: '12px'
                          }}>
                            <div style={{ fontSize: '0.85rem', color: theme.gray600, marginBottom: '4px' }}>
                              Security Deposit Amount
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: theme.gray900 }}>
                              ₹{costs.securityDeposit.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.gray600, marginTop: '4px' }}>
                              1/4th of total rent
                            </div>
                          </div>

                          <div style={{
                            padding: '16px',
                            background: theme.gray50,
                            borderRadius: '12px'
                          }}>
                            <div style={{ fontSize: '0.85rem', color: theme.gray600, marginBottom: '4px' }}>
                              GST
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: theme.gray900 }}>
                              ₹{costs.gst.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: theme.gray600, marginTop: '4px' }}>
                              18% on rental amount (excluding deposit)
                            </div>
                          </div>

                          <div style={{
                            padding: '20px',
                            background: `linear-gradient(135deg, ${selectedVenue.image}, ${selectedVenue.image}dd)`,
                            borderRadius: '12px',
                            color: theme.white
                          }}>
                            <div style={{ fontSize: '0.9rem', opacity: 0.95, marginBottom: '8px' }}>
                              TOTAL AMOUNT PAYABLE
                            </div>
                            <div style={{ fontSize: '2rem', fontWeight: '900' }}>
                              ₹{costs.total.toLocaleString()}
                            </div>
                          </div>

                          <div style={{
                            padding: '16px',
                            background: `${theme.warning}15`,
                            borderRadius: '12px',
                            border: `2px solid ${theme.warning}40`,
                            fontSize: '0.85rem',
                            color: theme.gray700,
                            lineHeight: 1.6
                          }}>
                            <div style={{ fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: theme.warning }}>
                              <Info size={16} />
                              NOTE
                            </div>
                            You should physically check the venue before proceeding with booking. Security deposit is refundable after event completion.
                          </div>

                          <button style={{
                            width: '100%',
                            background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
                            color: theme.white,
                            border: 'none',
                            padding: '16px',
                            borderRadius: '12px',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            marginTop: '16px',
                            opacity: isProcessingPayment ? 0.6 : 1
                          }}
                          onClick={() => {
                            // Validate form data
                            const eventNameInput = document.querySelector('input[placeholder="Enter event name"]') as HTMLInputElement;
                            const applicantNameInput = document.querySelector('input[placeholder="Enter full name"]') as HTMLInputElement;
                            const mobileInput = document.querySelector('input[placeholder="Enter mobile number"]') as HTMLInputElement;
                            const emailInput = document.querySelector('input[placeholder="Enter email address"]') as HTMLInputElement;

                            if (!bookingDates.from || !bookingDates.to) {
                              alert('Please select booking dates');
                              return;
                            }

                            if (!eventNameInput?.value || !applicantNameInput?.value || !mobileInput?.value || !emailInput?.value) {
                              alert('Please fill in all required fields');
                              return;
                            }

                            if (!/^\d{10}$/.test(mobileInput.value.replace(/\D/g, ''))) {
                              alert('Please enter a valid 10-digit mobile number');
                              return;
                            }

                            if (!/\S+@\S+\.\S+/.test(emailInput.value)) {
                              alert('Please enter a valid email address');
                              return;
                            }

                            const bookingData = {
                              eventName: eventNameInput.value,
                              applicantName: applicantNameInput.value,
                              mobile: mobileInput.value,
                              email: emailInput.value
                            };

                            processPayment(bookingData);
                          }}
                          disabled={isProcessingPayment}
                          >
                            {isProcessingPayment ? (
                              <>
                                <div style={{
                                  width: '20px',
                                  height: '20px',
                                  border: '3px solid white',
                                  borderTop: '3px solid transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }} />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard size={20} />
                                Proceed to Payment
                              </>
                            )}
                          </button>

                          <button
                            onClick={() => setShowBookingModal(false)}
                            style={{
                              width: '100%',
                              background: theme.white,
                              color: theme.gray700,
                              border: `2px solid ${theme.gray200}`,
                              padding: '12px',
                              borderRadius: '12px',
                              fontSize: '1rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Track Booking Modal */}
      {showTrackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '24px'
        }}
        onClick={() => setShowTrackModal(false)}>
          <div style={{
            background: theme.white,
            borderRadius: '24px',
            maxWidth: '600px',
            width: '100%',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '32px',
              borderBottom: `2px solid ${theme.gray100}`,
              background: `linear-gradient(135deg, ${theme.primaryPink}15, ${theme.secondaryBlue}15)`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: theme.gray900,
                  marginBottom: '8px'
                }}>
                  View My Bookings
                </h2>
                <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
                  Track your booking status
                </p>
              </div>
              <button
                onClick={() => setShowTrackModal(false)}
                style={{
                  background: theme.gray100,
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.gray700
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                  Booking Number *
                </label>
                <input
                  type="text"
                  placeholder="Enter Booking Number"
                  value={trackingData.bookingNumber}
                  onChange={(e) => setTrackingData({ ...trackingData, bookingNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${theme.gray200}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                  Applicant Mobile *
                </label>
                <input
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={trackingData.mobile}
                  onChange={(e) => setTrackingData({ ...trackingData, mobile: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${theme.gray200}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              {bookingResults && (
                <div style={{
                  padding: '20px',
                  background: `${theme.success}10`,
                  border: `2px solid ${theme.success}40`,
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <CheckCircle size={20} style={{ color: theme.success }} />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: theme.gray900 }}>
                      Booking Found!
                    </h3>
                  </div>
                  <div style={{ display: 'grid', gap: '12px', fontSize: '0.95rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.gray600 }}>Booking Number:</span>
                      <span style={{ fontWeight: '700' }}>{bookingResults.number}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.gray600 }}>Venue:</span>
                      <span style={{ fontWeight: '700' }}>{bookingResults.venue}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.gray600 }}>Event Date:</span>
                      <span style={{ fontWeight: '700' }}>{bookingResults.date}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.gray600 }}>Status:</span>
                      <span style={{
                        fontWeight: '700',
                        color: theme.success,
                        background: `${theme.success}20`,
                        padding: '4px 12px',
                        borderRadius: '6px'
                      }}>
                        {bookingResults.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.gray600 }}>Amount Paid:</span>
                      <span style={{ fontWeight: '700', color: theme.success }}>
                        ₹{bookingResults.amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    if (trackingData.bookingNumber && trackingData.mobile) {
                      // Simulate API call
                      setBookingResults({
                        number: trackingData.bookingNumber,
                        venue: 'Peoples Plaza',
                        date: '15-Dec-2025',
                        status: 'Confirmed',
                        amount: 141800
                      });
                    }
                  }}
                  style={{
                    flex: 1,
                    background: `linear-gradient(135deg, ${theme.primaryPink}, ${theme.accentPurple})`,
                    color: theme.white,
                    border: 'none',
                    padding: '14px',
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Search size={20} />
                  Search
                </button>
                <button
                  onClick={() => {
                    setShowTrackModal(false);
                    setBookingResults(null);
                    setTrackingData({ bookingNumber: '', mobile: '' });
                  }}
                  style={{
                    background: theme.white,
                    color: theme.gray700,
                    border: `2px solid ${theme.gray200}`,
                    padding: '14px 24px',
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '16px',
                background: `${theme.secondaryBlue}10`,
                borderRadius: '12px',
                border: `2px solid ${theme.secondaryBlue}40`,
                fontSize: '0.85rem',
                color: theme.gray700,
                lineHeight: 1.6
              }}>
                <div style={{ fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: theme.secondaryBlue }}>
                  <Info size={16} />
                  Additional Services
                </div>
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>Prepone / Postpone Booking (25% extra charge)</li>
                  <li>Cancel Booking (50% refund)</li>
                  <li>Download Receipt</li>
                  <li>View Payment History</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Check Availability Modal */}
      {showAvailabilityModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '24px',
          overflowY: 'auto'
        }}
        onClick={() => setShowAvailabilityModal(false)}>
          <div style={{
            background: theme.white,
            borderRadius: '24px',
            maxWidth: '1000px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '32px',
              borderBottom: `2px solid ${theme.gray100}`,
              background: `linear-gradient(135deg, ${theme.secondaryBlue}15, ${theme.primaryPink}15)`,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: theme.gray900,
                  marginBottom: '8px'
                }}>
                  Check Venue Availability
                </h2>
                <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
                  View real-time booking status across all venues
                </p>
              </div>
              <button
                onClick={() => setShowAvailabilityModal(false)}
                style={{
                  background: theme.gray100,
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.gray700
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              {/* Venue Selection */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '700', fontSize: '1.1rem', color: theme.gray900 }}>
                  Select Venue
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  {venues.map((venue, idx) => (
                   <button
                   key={idx}
                   onClick={() => {
                     setAvailabilityVenue(venue);
                     setAvailabilityMonth(new Date());
                     // Scroll to show the calendar after a brief delay
                     setTimeout(() => {
                       const modal = document.querySelector('[style*="maxHeight: 90vh"]');
                       if (modal) {
                         modal.scrollTo({ top: modal.scrollHeight, behavior: 'smooth' });
                       }
                     }, 100);
                   }}
                   style={{
                     padding: '16px',
                     background: availabilityVenue?.name === venue.name 
                       ? `linear-gradient(135deg, ${venue.image}15, ${venue.image}05)` 
                       : theme.white,
                     border: availabilityVenue?.name === venue.name 
                       ? `3px solid ${venue.image}` 
                       : `2px solid ${theme.gray200}`,
                     borderRadius: '12px',
                     cursor: 'pointer',
                     textAlign: 'left',
                     transition: 'all 0.3s ease'
                   }}
                      onMouseEnter={(e) => {
                        if (availabilityVenue?.name !== venue.name) {
                          e.currentTarget.style.borderColor = venue.image;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (availabilityVenue?.name !== venue.name) {
                          e.currentTarget.style.borderColor = theme.gray200;
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: `linear-gradient(135deg, ${venue.image}, ${venue.image}dd)`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: theme.white
                        }}>
                          <Building2 size={24} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: theme.gray900, fontSize: '1.1rem' }}>
                            {venue.name}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: theme.gray600 }}>
                            {venue.location}
                          </div>
                        </div>
                        {availabilityVenue?.name === venue.name && (
                          <CheckCircle size={24} style={{ color: venue.image }} />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Availability Calendar */}
              {availabilityVenue && (
                <div style={{
                  background: `${availabilityVenue.image}08`,
                  padding: '24px',
                  borderRadius: '16px',
                  border: `2px solid ${availabilityVenue.image}20`
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{
                      fontSize: '1.4rem',
                      fontWeight: '800',
                      color: theme.gray900
                    }}>
                      Availability Status - {availabilityMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => setAvailabilityMonth(new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth() - 1, 1))}
                        style={{
                          background: theme.white,
                          border: `2px solid ${theme.gray200}`,
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={() => setAvailabilityMonth(new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth() + 1, 1))}
                        style={{
                          background: theme.white,
                          border: `2px solid ${theme.gray200}`,
                          padding: '8px 16px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '8px',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} style={{
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        color: theme.gray700,
                        padding: '8px 0'
                      }}>
                        {day}
                      </div>
                    ))}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '8px',
                    textAlign: 'center'
                  }}>
                    {(() => {
                      const { firstDay, daysInMonth } = getMonthDays(availabilityMonth);
                      const days = [];
                      
                      // Empty cells for days before month starts
                      for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
                        days.push(<div key={`empty-${i}`} style={{ padding: '16px' }} />);
                      }
                      
                      // Calendar days
                      for (let day = 1; day <= daysInMonth; day++) {
                        const date = new Date(availabilityMonth.getFullYear(), availabilityMonth.getMonth(), day);
                        const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                        const isToday = new Date().getDate() === day && 
                                       new Date().getMonth() === availabilityMonth.getMonth() &&
                                       new Date().getFullYear() === availabilityMonth.getFullYear();
                        
                        // Simulate some booked dates (25, 26, 5 in red)
                        const isBooked = [25, 26, 5].includes(day);
                        
                        days.push(
                          <div
                            key={day}
                            style={{
                              padding: '16px',
                              borderRadius: '10px',
                              background: isPast 
                                ? theme.gray100 
                                : isBooked 
                                  ? theme.error 
                                  : isToday 
                                    ? availabilityVenue.image 
                                    : theme.gray900,
                              color: isPast ? theme.gray100 : theme.white,
                              cursor: isPast ? 'not-allowed' : 'pointer',
                              fontWeight: isToday ? '800' : '600',
                              fontSize: '1rem',
                              border: `2px solid ${isPast ? theme.gray200 : isBooked ? theme.error : theme.gray800}`,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isPast) {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {day}
                          </div>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>

                  {/* Legend */}
                  <div style={{
                    marginTop: '24px',
                    padding: '20px',
                    background: theme.white,
                    borderRadius: '12px',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: theme.gray900,
                        borderRadius: '6px',
                        border: `2px solid ${theme.gray800}`
                      }} />
                      <span style={{ fontSize: '0.9rem', fontWeight: '600', color: theme.gray700 }}>
                        Available - Black
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: theme.error,
                        borderRadius: '6px',
                        border: `2px solid ${theme.error}`
                      }} />
                      <span style={{ fontSize: '0.9rem', fontWeight: '600', color: theme.gray700 }}>
                        Not Available - Red
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        background: theme.gray100,
                        borderRadius: '6px',
                        border: `2px solid ${theme.gray200}`
                      }} />
                      <span style={{ fontSize: '0.9rem', fontWeight: '600', color: theme.gray700 }}>
                        Past Dates
                      </span>
                    </div>
                  </div>

                  {/* Important Notes */}
                  <div style={{
                    marginTop: '24px',
                    padding: '20px',
                    background: `${theme.warning}15`,
                    borderRadius: '12px',
                    border: `2px solid ${theme.warning}40`
                  }}>
                    <div style={{
                      fontWeight: '700',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: theme.warning,
                      fontSize: '1.1rem'
                    }}>
                      <Info size={20} />
                      IMPORTANT NOTES
                    </div>
                    <ul style={{
                      marginLeft: '20px',
                      color: theme.gray700,
                      fontSize: '0.9rem',
                      lineHeight: '1.8'
                    }}>
                      <li>You should physically check the venue before proceeding with booking</li>
                      <li>Black dates are available for booking</li>
                      <li>Red dates are already booked or not available</li>
                      <li>Booking confirmation is subject to payment completion</li>
                      <li>For urgent bookings, contact: 040-27018115</li>
                    </ul>
                  </div>
                </div>
              )}

{/* Important Notes */}
<div style={{
  marginTop: '24px',
  padding: '20px',
  background: `${theme.warning}15`,
  borderRadius: '12px',
  border: `2px solid ${theme.warning}40`
}}>
  <div style={{
    fontWeight: '700',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.warning,
    fontSize: '1.1rem'
  }}>
    <Info size={20} />
    IMPORTANT NOTES
  </div>
  <ul style={{
    marginLeft: '20px',
    color: theme.gray700,
    fontSize: '0.9rem',
    lineHeight: '1.8'
  }}>
    <li>You should physically check the venue before proceeding with booking</li>
    <li>Black dates are available for booking</li>
    <li>Red dates are already booked or not available</li>
    <li>Booking confirmation is subject to payment completion</li>
    <li>For urgent bookings, contact: 040-27018115</li>
  </ul>
</div>

{/* Proceed to Booking Button - Only show when venue is selected */}
{availabilityVenue && (
  <button
    onClick={() => {
      setShowAvailabilityModal(false);
      setSelectedVenue(availabilityVenue);
      setShowBookingModal(true);
    }}
    style={{
      width: '100%',
      marginTop: '24px',
      background: `linear-gradient(135deg, ${availabilityVenue.image}, ${availabilityVenue.image}dd)`,
      color: theme.white,
      border: 'none',
      padding: '18px',
      borderRadius: '12px',
      fontSize: '1.15rem',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 15px ${availabilityVenue.image}40`
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 6px 20px ${availabilityVenue.image}60`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = `0 4px 15px ${availabilityVenue.image}40`;
    }}
  >
    <Calendar size={22} />
    Proceed to Booking
    <ArrowRight size={22} />
  </button>
)}


              {!availabilityVenue && (
                <div style={{
                  padding: '60px',
                  textAlign: 'center',
                  color: theme.gray600
                }}>
                  <Calendar size={64} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                  <p style={{ fontSize: '1.2rem', fontWeight: '600' }}>
                    Please select a venue to check availability
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HMDAPortal;